import { CreateQuestionnaireDashboardData } from "api/questionnaireHelpers";
import CONSTANTS from "core/constants";
import { QuestionnaireBasic } from "core/types/index";
import { format } from "date-fns";
import React from "react";
import {
  Dot,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { sortBy } from "remeda";
import { styled, useTheme } from "styled-components";

export type OpenSingleQuestionnaire = {
  isNew: boolean;
  clinicalId: string;
  requestId: string;
};

export const DashboardLinearGraph = ({
  answers,
  questionnaire,
  openSingleQuestionnaire,
}: {
  answers: CreateQuestionnaireDashboardData[keyof CreateQuestionnaireDashboardData];
  questionnaire: QuestionnaireBasic;
  openSingleQuestionnaire: (value: OpenSingleQuestionnaire) => void;
}) => {
  const data2Use = sortBy(
    answers.map((d) => ({
      date: new Date(d.completedAt ?? "").getTime(),
      score: d.score,
      isNew: d.isNew,
      id: d.id,
    })),
    (d) => d.date,
  );

  const mindlerTheme = useTheme();

  const maxScore = Math.max(...questionnaire.scoreSteps.map((s) => s.boundary));
  const areas = questionnaire.scoreSteps.map((s, i) => ({
    y1: questionnaire.scoreSteps[i - 1]?.boundary ?? 0,
    y2: s.boundary,
    fill: s.statusColor.hex,
  }));

  const minDate = Math.min(...data2Use.map((d) => d.date)) ?? 0;
  const maxDate = Math.max(...data2Use.map((d) => d.date)) ?? 0;

  const ticks = [0, ...areas.map((area) => area.y2)];
  return (
    <GraphFrame>
      <ResponsiveContainer width="100%" aspect={1.5}>
        <LineChart
          onClick={(event) => {
            if (event.activeTooltipIndex) {
              openSingleQuestionnaire({
                clinicalId: questionnaire.clinicalId,
                isNew: data2Use[event.activeTooltipIndex].isNew,
                requestId: data2Use[event.activeTooltipIndex].id,
              });
            }
          }}
          data={data2Use}
          margin={{ top: 5, right: 30, left: 0, bottom: 0 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={(_) => ""}
            type="number"
            domain={[minDate, maxDate]}
          />
          <YAxis domain={[0, maxScore]} type="number" ticks={ticks} />
          {areas.map((area, i) => (
            <React.Fragment key={i}>
              <ReferenceArea {...area} fillOpacity={0.15} />
              <ReferenceLine
                y={area.y2}
                stroke={mindlerTheme.COLORS.GRAY_DARK}
                strokeDasharray="2 3"
                strokeWidth={1.25}
                strokeOpacity={0.333}
              />
            </React.Fragment>
          ))}
          <Tooltip
            labelFormatter={(date) =>
              format(new Date(date), CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT)
            }
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke={mindlerTheme.COLORS.GRAY_DARK}
            strokeWidth={1.25}
            dot={({ cx, cy, strokeWidth, payload }) => {
              return (
                <Dot
                  cx={cx}
                  cy={cy}
                  r={4}
                  stroke={
                    payload.isNew
                      ? mindlerTheme.COLORS.ACTION_BLUE
                      : mindlerTheme.COLORS.GRAY_DARK
                  }
                  strokeWidth={payload.isNew ? strokeWidth * 2 : strokeWidth}
                  fill={mindlerTheme.COLORS.WHITE}
                />
              );
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </GraphFrame>
  );
};

const GraphFrame = styled.div`
  display: block;
  position: relative;
`;
